import { Flex, Box, Text, useToast, PseudoBox } from '@chakra-ui/core'
import { NoFocusButton } from 'components/CommonComponents/NoFocusButton'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { JobsTable } from './JobsTable'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { AppDispatch } from 'config/redux'
import keys from 'constants/keys'
import { getJobs } from 'redux/actions/admin'
import { useJobsModals } from './JobsModalsManagerContext'
import { FilterByService } from './JobsFilters/FilterByService'
import { FilterByFrequency } from './JobsFilters/FilterByFrequency'
import { format } from 'date-fns'
import { NavButtonTabs, TabKey } from 'components/CommonComponents/AudienceTabs'
import { RefreshCcw } from 'lucide-react'

export interface DateRangeState {
  startDate: Date | null
  endDate: Date | null
}

export default function JobsBoardContent() {
  const toast = useToast()
  const { openJobModal } = useJobsModals()
  const dispatch: AppDispatch = useDispatch()

  const theme = useSelector((state: RootState) => state.general.theme)
  const { isConfigReady } = useSelector((state: RootState) => state.config)
  const { activeJobs, inactiveJobs } = useSelector((state: RootState) => state.admin)

  const [status, setStatus] = useState<TabKey>('active')
  const [loading, setLoading] = useState(true)
  const [selectedService, setSelectedService] = useState('all')
  const [selectedFrequency, setSelectedFrequency] = useState('all')
  const [dateRange, setDateRange] = useState<DateRangeState>({
    startDate: new Date(),
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
  })

  const t_jobs = useLocaleText('jobs', true)
  const t_active = useLocaleText('active', true)
  const t_inactive = useLocaleText('inactive', true)
  const create_new_job = useLocaleText('job_create', true)

  const rawJobs = status === 'active' ? activeJobs : inactiveJobs

  const filteredJobs = useMemo(() => {
    return rawJobs
      .filter((job) => {
        const matchesService = selectedService === 'all' || job.action_service === selectedService
        const matchesFrequency = selectedFrequency === 'all' || job.recurrence_frequency === parseInt(selectedFrequency)

        return matchesService && matchesFrequency
      })
      .sort((a, b) => {
        if (a.next_occurrence && b.next_occurrence) {
          return a.next_occurrence.localeCompare(b.next_occurrence)
        }

        if (!a.next_occurrence && b.next_occurrence) return 1
        if (a.next_occurrence && !b.next_occurrence) return -1
        return 0
      })
  }, [rawJobs, selectedService, selectedFrequency])

  const loadJobs = useCallback(
    async (currentStatus: TabKey, currentDateRange?: DateRangeState) => {
      setLoading(true)
      try {
        const params: any = { status: currentStatus === 'active' ? keys.JOBS_STATUS.ACTIVE : keys.JOBS_STATUS.INACTIVE }

        if (currentDateRange?.startDate && currentDateRange?.endDate) {
          params.from_ts = format(currentDateRange.startDate, 'yyyyMMddHHmmss')
          params.to_ts = format(currentDateRange.endDate, 'yyyyMMddHHmmss')
        }

        await dispatch(getJobs(params))
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to get jobs',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
      } finally {
        setLoading(false)
      }
    },
    [dispatch, toast]
  )

  const handleDateRangeChange = useCallback(
    async (newDateRange: DateRangeState) => {
      setDateRange(newDateRange)
      await loadJobs(status as TabKey, newDateRange)
    },
    [status, loadJobs]
  )

  useEffect(() => {
    if (isConfigReady) {
      loadJobs(status as TabKey, dateRange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfigReady, status])

  const handleToggle = () => {
    setStatus(status === 'active' ? 'inactive' : 'active')
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Flex align="center" style={{ gap: '10px' }}>
          <Text fontSize="2xl" fontWeight="bold">
            {t_jobs}
          </Text>
          <PseudoBox
            as="button"
            onClick={() => loadJobs(status as TabKey, dateRange)}
            color={theme?.elementsColor}
            _hover={{ cursor: 'pointer' }}
          >
            <RefreshCcw size={20} />
          </PseudoBox>
        </Flex>
        <NoFocusButton
          w="max-content"
          maxWidth="200px"
          bg={theme?.elementsColor}
          color="white"
          rounded="md"
          background={theme?.elementsColor}
          onClick={() => openJobModal()}
          style={{ gap: '5px' }}
          _hover={{ opacity: 0.8 }}
        >
          <Text fontSize="14px">{create_new_job}</Text>
        </NoFocusButton>
      </Flex>
      <Flex
        justify="space-between"
        align="center"
        mb={4}
        flexWrap={{ base: 'wrap', md: 'nowrap' }}
        style={{ gap: '10px' }}
      >
        <Box w="100%" maxWidth={{ base: '100%', lg: '400px' }}>
          <NavButtonTabs
            value={status}
            onChange={handleToggle}
            tabs={[
              { key: 'active', label: t_active },
              { key: 'inactive', label: t_inactive },
            ]}
          />
        </Box>

        <Flex style={{ gap: '10px' }} align="center" flexWrap={{ base: 'wrap', md: 'nowrap' }} mt={{ base: 4, md: 0 }}>
          <FilterByService selectedService={selectedService} setSelectedService={setSelectedService} />
          <FilterByFrequency selectedFrequency={selectedFrequency} setSelectedFrequency={setSelectedFrequency} />
        </Flex>
      </Flex>

      <JobsTable
        jobs={filteredJobs}
        status={status === 'active' ? keys.JOBS_STATUS.ACTIVE : keys.JOBS_STATUS.INACTIVE}
        loading={loading}
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
      />
    </Box>
  )
}
